// src/pages/landing/TermsOfServicePage.js
import React from 'react';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, TYPE, SP, W } from '../../components/landing/tokens';

const LAST_UPDATED = '27 June 2026';

const TermsOfServicePage = () => {
    const { isRTL } = useLanguage();

    // F16 — skin only. The legal prose is deliberate text and is not touched.
    const sectionTitle = { ...TYPE.title, color: C.ink };
    const body = { ...TYPE.body, color: C.inkMuted };
    const legalLink = { color: C.ink, textDecoration: 'none', borderBlockEnd: `1px solid ${C.lineStrong}` };

    return (
        <div style={{ background: C.paper, color: C.ink }} dir={isRTL ? 'rtl' : 'ltr'}>
            <LandingLayout>
                {/* Hero */}
                <section className="py-16 lg:py-24" style={{ background: C.paperDeep }}>
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 style={{ ...TYPE.displayM, color: C.ink, marginBlockEnd: SP.sm }}>
                            Terms of Service
                        </h1>
                        <p style={{ ...TYPE.caption, color: C.inkFaint }}>
                            Last updated: {LAST_UPDATED}
                        </p>
                    </div>
                </section>

                {/* Content */}
                <section className="py-16" style={{ background: C.paper }}>
                    <div className="mx-auto px-4 sm:px-6 lg:px-8 space-y-10" style={{ maxWidth: W.prose }}>

                        <div>
                            <p className="leading-relaxed" style={body}>
                                These Terms of Service ("Terms") govern your access to and use of the Aqana
                                point-of-sale and business analytics platform (the "Service"), operated by{' '}
                                <strong>AQANA FINANCE LTD</strong> ("Aqana", "we", "us", or "our"), a company
                                registered in England &amp; Wales. By accessing or using the Service, you agree to be
                                bound by these Terms.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>1. Acceptance of Terms</h2>
                            <p className="leading-relaxed" style={body}>
                                By creating an account or using the Service, you confirm that you have the authority to
                                enter into these Terms on behalf of your business and that you accept them in full. If
                                you do not agree, you must not use the Service.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>2. The Service</h2>
                            <p className="leading-relaxed" style={body}>
                                Aqana provides point-of-sale, operations management, and analytics tools for
                                restaurants and hospitality businesses, including optional integrations with
                                third-party platforms such as Facebook and Instagram for organic social analytics. We
                                may add, change, or remove features over time to improve the Service.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>3. Accounts &amp; Responsibilities</h2>
                            <p className="leading-relaxed" style={body}>
                                You are responsible for maintaining the confidentiality of your account credentials and
                                for all activity that occurs under your account. You agree to provide accurate
                                information and to keep it up to date. You must notify us promptly of any unauthorised
                                use of your account.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>4. Acceptable Use</h2>
                            <p className="leading-relaxed" style={body}>
                                You agree not to misuse the Service, including by attempting to access it through
                                unauthorised means, interfering with its operation, infringing the rights of others, or
                                using it for any unlawful purpose. You are responsible for ensuring your use of any
                                connected third-party accounts complies with those platforms' terms.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>5. Intellectual Property</h2>
                            <p className="leading-relaxed" style={body}>
                                The Service, including its software, design, and content, is owned by AQANA FINANCE LTD
                                and protected by intellectual property laws. You retain ownership of the business data
                                you enter into the Service. We are granted a limited licence to process that data solely
                                to provide the Service to you.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>6. Third-Party Services</h2>
                            <p className="leading-relaxed" style={body}>
                                When you connect a third-party account such as Facebook or Instagram, your use of that
                                integration is also subject to the relevant platform's terms and policies. We are not
                                responsible for the availability, accuracy, or practices of third-party services.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>7. Disclaimers &amp; Limitation of Liability</h2>
                            <p className="leading-relaxed" style={body}>
                                The Service is provided "as is" and "as available" without warranties of any kind, to
                                the fullest extent permitted by law. To the maximum extent permitted by law, AQANA
                                FINANCE LTD shall not be liable for any indirect, incidental, or consequential damages,
                                or for any loss of profits, revenue, or data, arising from your use of the Service.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>8. Termination</h2>
                            <p className="leading-relaxed" style={body}>
                                You may stop using the Service and close your account at any time. We may suspend or
                                terminate access if you breach these Terms or use the Service in a way that risks harm
                                to us, other users, or third parties. On termination, your data is handled in accordance
                                with our Privacy Policy.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>9. Governing Law</h2>
                            <p className="leading-relaxed" style={body}>
                                These Terms are governed by the laws of England &amp; Wales, and any disputes shall be
                                subject to the exclusive jurisdiction of the courts of England &amp; Wales.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>10. Contact Us</h2>
                            <p className="leading-relaxed" style={body}>
                                Questions about these Terms can be sent to{' '}
                                <a href="mailto:admin@aqana.uk" style={legalLink}>admin@aqana.uk</a>.
                            </p>
                        </div>

                    </div>
                </section>
            </LandingLayout>
        </div>
    );
};

export default TermsOfServicePage;
